import { useDeepMemo } from '../utils/reactUtils';
import { calculateOverallCompletion, OverallCompletionData } from '../utils/completionCalculations';
import { DocumentCategory, DocumentSubcategory, DocumentType, DocumentField, Document } from '../types/Document';
import { UserDetails } from '../types/User';

// Pure composition layer for derived data (completion stats, enhanced categories, etc.).
// Accepts all required data as arguments, does not fetch or maintain state.

interface UseDocumentDataParams {
  categories: DocumentCategory[];
  subcategories: DocumentSubcategory[];
  documentTypes: DocumentType[];
  documentFields: DocumentField[];
  userDocuments: Document[];
  userFieldValues: { [key: string]: any };
  selectedUser: UserDetails | null;
}

interface CategoryWithStats extends DocumentCategory {
  stats: {
    totalItems: number;
    completedItems: number;
    missingItems: number;
    expiredItems: number;
    completionRate: number;
    subcategories: any;
  };
}

interface UseDocumentDataResult {
  completionStats: OverallCompletionData | null;
  categoriesWithStats: CategoryWithStats[];
  overallStats: {
    totalItems: number;
    completedItems: number;
    missingItems: number;
    expiredItems: number;
    completionRate: number;
  };
}

export const useDocumentData = ({
  categories,
  subcategories,
  documentTypes,
  documentFields,
  userDocuments,
  userFieldValues,
  selectedUser
}: UseDocumentDataParams): UseDocumentDataResult => {
  // Memoize completion stats
  const completionStats = useDeepMemo(() => {
    // Only require categories and selectedUser - subcategories are now optional!
    if (!categories.length || !selectedUser) return null;
    return calculateOverallCompletion(
      categories,
      subcategories,
      documentTypes,
      documentFields,
      userDocuments,
      userFieldValues
    );
  }, [
    categories,
    subcategories,
    documentTypes,
    documentFields,
    userDocuments,
    userFieldValues,
    selectedUser?.id
  ]);

  // Enhanced categories with stats
  const categoriesWithStats = useDeepMemo((): CategoryWithStats[] => {
    if (!completionStats) return [];
    return categories.map((category: DocumentCategory): CategoryWithStats => ({
      ...category,
      stats: completionStats.categories[category.id] || {
        totalItems: 0,
        completedItems: 0,
        missingItems: 0,
        expiredItems: 0,
        completionRate: 0,
        subcategories: {}
      }
    }));
  }, [categories, completionStats]);

  const overallStats = useDeepMemo(() => {
    return completionStats?.overall || {
      totalItems: 0,
      completedItems: 0,
      missingItems: 0,
      expiredItems: 0,
      completionRate: 0
    };
  }, [completionStats]);

  return useDeepMemo(() => ({
    completionStats,
    categoriesWithStats,
    overallStats
  }), [completionStats, categoriesWithStats, overallStats]);
};
